import { Grid, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import FlowSidebarItem from './FlowSidebarItem';
import FlowSidebarSelection from './FlowSidebarSelection';

const FlowSidebar = ({
  nodes,
  campaignMode,
  selection,
  updateItem,
  campaigns,
  webhooks,
  emails,
  availableCustomFields,
  textMessages,
  viberMessages,
  pushNotifications,
  eventTags,
  eventActions,
  flowTypeOptions,
  readOnly,
  pages,
  subscriptions,
  segments,
  whatsappMessages,
  surveys,
  remainingDailyQuotaWhatsappMessages,
}) => {
  const daysOfweek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const selectedNodes = nodes.filter((node) => selection.nodes.includes(node.id));
  const selectedNode = selectedNodes[selectedNodes.length - 1];
  const googleAdNodeExists = nodes.some((node) => node.data?.custom?.key === 'sendGoogleAd');
  // Google Ads are continuous-only. Offer the step only in continuous flows (and one per flow).
  const showGoogleAdStep = campaignMode === 'continuous';

  return (
    <aside data-tour="addVisualElement">
      {selectedNode ? (
        <FlowSidebarSelection
          flowTypeOptions={flowTypeOptions}
          selectedNode={selectedNode}
          emails={emails}
          textMessages={textMessages}
          viberMessages={viberMessages}
          pushNotifications={pushNotifications}
          campaigns={campaigns}
          webhooks={webhooks}
          updateItem={updateItem}
          availableCustomFields={availableCustomFields}
          eventTags={eventTags}
          eventActions={eventActions}
          readOnly={readOnly}
          pages={pages}
          subscriptions={subscriptions}
          segments={segments}
          whatsappMessages={whatsappMessages}
          surveys={surveys}
          remainingDailyQuotaWhatsappMessages={remainingDailyQuotaWhatsappMessages}
        />
      ) : (
        <>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={4}>
            Messages
          </Text>
          <Grid>
            <FlowSidebarItem
              type="default"
              data={{
                label: 'Email',
                custom: {
                  key: 'sendEmail',
                  config: [
                    { field: 'id', name: 'Email', type: 'select' },
                    { name: 'Send on', type: 'sectionStart', overview: { name: '' } },
                    ...daysOfweek.map((dayOfWeek) => {
                      return {
                        field: `runOn${dayOfWeek}`,
                        name: `${dayOfWeek}`,
                        type: 'checkbox',
                        overview: { name: dayOfWeek.substring(0, 3), inline: true },
                      };
                    }),
                    { name: '', type: 'sectionStart' },
                    { field: `observeQuietHours`, name: `Observe quiet hours`, type: 'checkbox' },
                  ],
                  values: daysOfweek.reduce(
                    (days, day) => {
                      days[`runOn${day}`] = true;
                      return days;
                    },
                    { observeQuietHours: true }
                  ),
                  labels: {},
                },
              }}
            />
            <FlowSidebarItem
              type="default"
              data={{
                label: 'Push',
                custom: {
                  key: 'sendPushNotification',
                  config: [
                    { field: 'id', name: 'Push Notification', type: 'select' },
                    { name: 'Send on', type: 'sectionStart', overview: { name: '' } },
                    ...daysOfweek.map((dayOfWeek) => {
                      return {
                        field: `runOn${dayOfWeek}`,
                        name: `${dayOfWeek}`,
                        type: 'checkbox',
                        overview: { name: dayOfWeek.substring(0, 3), inline: true },
                      };
                    }),
                    { name: '', type: 'sectionStart' },
                    { field: `observeQuietHours`, name: `Observe quiet hours`, type: 'checkbox' },
                  ],
                  values: daysOfweek.reduce(
                    (days, day) => {
                      days[`runOn${day}`] = true;
                      return days;
                    },
                    { observeQuietHours: true }
                  ),
                  labels: {},
                },
              }}
            />
            <FlowSidebarItem
              type="default"
              data={{
                label: 'Viber',
                custom: {
                  key: 'sendViberMessage',
                  config: [
                    { field: 'id', name: 'Viber Message', type: 'select' },
                    { name: 'Send on', type: 'sectionStart', overview: { name: '' } },
                    ...daysOfweek.map((dayOfWeek) => {
                      return {
                        field: `runOn${dayOfWeek}`,
                        name: `${dayOfWeek}`,
                        type: 'checkbox',
                        overview: { name: dayOfWeek.substring(0, 3), inline: true },
                      };
                    }),
                    { name: '', type: 'sectionStart' },
                    { field: `observeQuietHours`, name: `Observe quiet hours`, type: 'checkbox' },
                  ],
                  values: daysOfweek.reduce(
                    (days, day) => {
                      days[`runOn${day}`] = true;
                      return days;
                    },
                    { observeQuietHours: true }
                  ),
                  labels: {},
                },
              }}
            />
            <FlowSidebarItem
              type="default"
              data={{
                label: 'WhatsApp',
                custom: {
                  key: 'sendWhatsappMessage',
                  config: [
                    { field: 'id', name: 'WhatsApp Message', type: 'select' },
                    { name: 'Send on', type: 'sectionStart', overview: { name: '' } },
                    ...daysOfweek.map((dayOfWeek) => {
                      return {
                        field: `runOn${dayOfWeek}`,
                        name: `${dayOfWeek}`,
                        type: 'checkbox',
                        overview: { name: dayOfWeek.substring(0, 3), inline: true },
                      };
                    }),
                    { name: '', type: 'sectionStart' },
                    { field: `observeQuietHours`, name: `Observe quiet hours`, type: 'checkbox' },
                  ],
                  values: daysOfweek.reduce(
                    (days, day) => {
                      days[`runOn${day}`] = true;
                      return days;
                    },
                    { observeQuietHours: true }
                  ),
                  labels: {},
                  disclaimer:
                    'Please note that messages created during the last hour are not visible/selectable due to WhatsApp API limitations!',
                  disclaimerLimit: true,
                },
              }}
            />
            <FlowSidebarItem
              type="default"
              data={{
                label: 'Text',
                custom: {
                  key: 'sendTextMessage',
                  config: [
                    { field: 'id', name: 'Text Message', type: 'select' },
                    { name: 'Send on', type: 'sectionStart', overview: { name: '' } },
                    ...daysOfweek.map((dayOfWeek) => {
                      return {
                        field: `runOn${dayOfWeek}`,
                        name: `${dayOfWeek}`,
                        type: 'checkbox',
                        overview: { name: dayOfWeek.substring(0, 3), inline: true },
                      };
                    }),
                    { name: '', type: 'sectionStart' },
                    { field: `observeQuietHours`, name: `Observe quiet hours`, type: 'checkbox' },
                  ],
                  values: daysOfweek.reduce(
                    (days, day) => {
                      days[`runOn${day}`] = true;
                      return days;
                    },
                    { observeQuietHours: true }
                  ),
                  labels: {},
                },
              }}
            />
          </Grid>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mt="sm" mb={4}>
            Ads
          </Text>
          <Grid>
            <FlowSidebarItem
              type="default"
              data={{
                label: 'Facebook',
                custom: {
                  key: 'sendFacebookAd',
                  config: [
                    { field: 'id', name: 'Facebook Ad', type: 'select' },
                    {
                      field: `includeLookalikes`,
                      name: 'Include lookalikes',
                      type: 'checkbox',
                    },
                    {
                      field: 'lookalikeType',
                      name: 'Lookalike type',
                      type: 'select',
                    },
                    {
                      field: 'lookalikeRatio',
                      name: 'Custom ratio value',
                      type: 'select',
                    },
                    { name: 'Send on', type: 'sectionStart', overview: { name: '' } },

                    ...daysOfweek.map((dayOfWeek) => {
                      return {
                        field: `runOn${dayOfWeek}`,
                        name: `${dayOfWeek}`,
                        type: 'checkbox',
                        overview: { name: dayOfWeek.substring(0, 3), inline: true },
                      };
                    }),
                    { name: '', type: 'sectionStart' },
                    { field: `observeQuietHours`, name: `Observe quiet hours`, type: 'checkbox' },
                  ],
                  values: daysOfweek.reduce(
                    (days, day) => {
                      days[`runOn${day}`] = true;
                      return days;
                    },
                    { observeQuietHours: true, lookalikeType: 'reach' }
                  ),
                  labels: {},
                },
              }}
            />
            {showGoogleAdStep && (
              <FlowSidebarItem
                type="default"
                disabled={googleAdNodeExists}
                data={{
                  label: 'Google',
                  custom: {
                    key: 'sendGoogleAd',
                    config: [
                      { field: 'id', name: 'Google Ad', type: 'select' },
                      {
                        field: 'membershipDurationDays',
                        name: 'Membership Duration',
                        type: 'number',
                        default: 30,
                        min: 1,
                        max: 540,
                        detailsSuffix: 'days',
                      },
                      { name: 'Send on', type: 'sectionStart', overview: { name: '' } },
                      ...daysOfweek.map((dayOfWeek) => {
                        return {
                          field: `runOn${dayOfWeek}`,
                          name: `${dayOfWeek}`,
                          type: 'checkbox',
                          overview: { name: dayOfWeek.substring(0, 3), inline: true },
                        };
                      }),
                      { name: '', type: 'sectionStart' },
                      { field: `observeQuietHours`, name: `Observe quiet hours`, type: 'checkbox' },
                    ],
                    values: daysOfweek.reduce(
                      (days, day) => {
                        days[`runOn${day}`] = true;
                        return days;
                      },
                      {
                        observeQuietHours: true,
                        membershipDurationDays: 30,
                      }
                    ),
                    labels: {},
                  },
                }}
              />
            )}
          </Grid>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mt="sm" mb={4}>
            Logic
          </Text>
          <Grid>
            <FlowSidebarItem
              type="default"
              data={{
                label: 'Wait',
                custom: {
                  key: 'wait',
                  config: [
                    { field: 'waitMode', name: 'Mode', type: 'select' },
                    {
                      field: 'durationInHours',
                      name: 'Duration',
                      type: 'number',
                      detailsSuffix: 'hours',
                      showWhen: { field: 'waitMode', value: 'static' },
                    },
                    {
                      field: 'mergeTag',
                      name: 'Date merge tag',
                      type: 'string',
                      showWhen: { field: 'waitMode', value: 'dynamic' },
                    },
                    {
                      field: 'offsetHours',
                      name: 'Offset',
                      type: 'number',
                      min: 0,
                      detailsSuffix: 'hours before',
                      showWhen: { field: 'waitMode', value: 'dynamic' },
                    },
                  ],
                  values: { waitMode: 'static' },
                  labels: {},
                },
              }}
            />
            <FlowSidebarItem
              type="conditional"
              data={{
                label: 'Condition',
                custom: {
                  key: 'condition',
                  config: [{ field: 'rules', type: 'segment' }],
                  values: {},
                  labels: {},
                },
              }}
            />
            <FlowSidebarItem
              type="abSplit"
              data={{
                label: 'A/B split',
                custom: {
                  key: 'abSplit',
                  values: {},
                  labels: {},
                  config: [],
                },
              }}
            />
          </Grid>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mt="sm" mb={4}>
            Custom
          </Text>
          <Grid>
            <FlowSidebarItem
              type="default"
              data={{
                label: 'Webhook',
                custom: {
                  key: 'sendWebhook',
                  config: [
                    { field: 'id', name: 'Webhook', type: 'select' },
                    { name: 'Send on', type: 'sectionStart', overview: { name: '' } },
                    ...daysOfweek.map((dayOfWeek) => {
                      return {
                        field: `runOn${dayOfWeek}`,
                        name: `${dayOfWeek}`,
                        type: 'checkbox',
                        overview: { name: dayOfWeek.substring(0, 3), inline: true },
                      };
                    }),
                    { name: '', type: 'sectionStart' },
                    { field: `observeQuietHours`, name: `Observe quiet hours`, type: 'checkbox' },
                  ],
                  values: daysOfweek.reduce(
                    (days, day) => {
                      days[`runOn${day}`] = true;
                      return days;
                    },
                    { observeQuietHours: true }
                  ),
                  labels: {},
                },
              }}
            />
          </Grid>
          <Text size="xs" c="dimmed" mt="auto" pt="sm">
            <IconInfoCircle size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Drag these onto the canvas to set up the campaign flow. Click to select and modify, del to delete.
          </Text>
        </>
      )}
    </aside>
  );
};

export default FlowSidebar;
